import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  tardekConfig,
  createByEnvironmentFilter,
  isSupportedDeclarationTypeCountry,
  NctsCountry,
} from 'config'
import { ISelectOption } from 'types/IClassifier'
import axios from 'axios'
import { CodelistResponse, CreateOrUpdateTransitOperationRequestDeclarationTypeEnum } from 'api/models'
import { CodelistGroup, getCountryForOffice, mapSelectOptions } from 'features/declaration/codelist/models/types'
import { User } from 'routes/Customer/services/user.service'
import { codelistQueryKeys } from '../keys'

const baseUrl = process.env.REACT_APP_API_URL

export function getEnabledCountriesForEnvironment() {
  return tardekConfig.getCountries(createByEnvironmentFilter(process.env.REACT_APP_ENV === 'live' ? 'live' : 'test'))
}

function isNotAdminOnlyCountry(country: NctsCountry) {
  return !tardekConfig.adminOnlyCountries.includes(country)
}

function isAdminOnlyCountryWhileAdminUser(country: NctsCountry, user?: User | undefined) {
  return tardekConfig.adminOnlyCountries.includes(country) && user?.role === 'ADMIN'
}

export type CodelistFilterFn = (item: CodelistResponse) => boolean
export type CallbackForCodelistFilterFn = () => CodelistFilterFn

export const createDepartureCustomsOfficesBySupportedCountriesAndUserFilter = (
  user?: User | undefined,
) => (item: CodelistResponse) => {
  const officeCountry = getCountryForOffice(item) as NctsCountry

  return (
    getEnabledCountriesForEnvironment().includes(officeCountry)
    && (isNotAdminOnlyCountry(officeCountry) || isAdminOnlyCountryWhileAdminUser(officeCountry, user))
  )
}

export const createDepartureCustomsOfficesBySupportedCountriesFilter = () => (item: CodelistResponse) => {
  const officeCountry = getCountryForOffice(item) as NctsCountry

  return getEnabledCountriesForEnvironment().includes(officeCountry) && isNotAdminOnlyCountry(officeCountry)
}

export const createDepartureCustomsOfficesByDeclarationTypeAndUserFilter = (
  declarationType: string | undefined,
  user?: User | undefined,
) => (item: CodelistResponse) => {
  const officeCountry = getCountryForOffice(item) as NctsCountry
  const declarationTypeEnum = CreateOrUpdateTransitOperationRequestDeclarationTypeEnum[
    declarationType as keyof typeof CreateOrUpdateTransitOperationRequestDeclarationTypeEnum]

  return (
    getEnabledCountriesForEnvironment().includes(officeCountry)
      && (isNotAdminOnlyCountry(officeCountry) || isAdminOnlyCountryWhileAdminUser(officeCountry, user))
      && declarationType != null
      && isSupportedDeclarationTypeCountry(officeCountry, declarationTypeEnum)
  )
}

export const createTypeOfIdentificationsByModeOfTransportFilter = (
  modeOfTransportWithPrefix: string,
) => (
  item: CodelistResponse,
) => !!item?.code?.startsWith(`TYPE_OF_IDENTIFICATION_${modeOfTransportWithPrefix?.substring('TRANSPORT_MODE_'.length)}`)

function useCodelist(group: CodelistGroup, callback?: CallbackForCodelistFilterFn): [CodelistResponse[], ISelectOption[]] {
  const { i18n } = useTranslation()

  const {
    data,
    isFetching,
  } = useQuery(
    {
      queryKey: codelistQueryKeys.group(group),
      queryFn: async () => (await axios.get<CodelistResponse[]>(`${baseUrl}/v2/codelist/group/${group ?? ''}`)).data,
    },
  )

  const codelistAsOptions = useMemo(
    () => mapSelectOptions(callback ? data?.filter(callback()) : data, i18n.language),
    [group, isFetching, callback, data],
  )

  return [
    data ?? [],
    codelistAsOptions,
  ]
}

export default useCodelist
